import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { FileCheck2, ExternalLink, Send, Upload, Ban, RefreshCw, Loader2 } from "lucide-react";

const STATI = {
  generato: { testo: "Generato", cls: "bg-[#f9f8f5] text-[#1A2B4A] border-[#1A2B4A]/15" },
  da_salvare: { testo: "Da salvare su Drive", cls: "bg-[#FDECEC] text-[#9b2c2c] border-[#9b2c2c]/25" },
  inviato: { testo: "Inviato", cls: "bg-[#FDFAF1] text-[#7a5c12] border-[#C9A84C]/50" },
  firmato: { testo: "Firmato", cls: "bg-[#E7F2EC] text-[#1F5C42] border-[#1F5C42]/25" },
  annullato: { testo: "Annullato", cls: "bg-white text-[#8a93a6] border-[#8a93a6]/30" },
};

export default function DocumentiGenerati({ clienteId, praticaId, titolo, nota }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState("");
  const firmato = useRef(null);
  const riprova = useRef(null);
  const [target, setTarget] = useState(null);

  const { data: righe = [], refetch } = useQuery({
    queryKey: ["documenti-generati", clienteId || "", praticaId || ""],
    queryFn: async () => (await api.get("/documenti/generati", {
      params: { cliente_id: clienteId || undefined, pratica_id: praticaId || undefined },
    })).data,
  });

  const aggiorna = () => { refetch(); qc.invalidateQueries({ queryKey: ["documenti-generati"] }); };

  const cambiaStato = async (r, stato) => {
    setBusy(r.id);
    try {
      await api.post(`/documenti/generati/${r.id}/stato`, { stato });
      toast.success(stato === "inviato" ? "Segnato come inviato" : "Documento annullato");
      aggiorna();
    } catch { toast.error("Non riuscito"); } finally { setBusy(""); }
  };

  const invia = async (file, tipo) => {
    if (!file || !target) return;
    setBusy(target.id);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/documenti/generati/${target.id}/${tipo}`, fd);
      toast.success(tipo === "firmato" ? "PDF firmato caricato su Drive" : "Salvato su Drive");
      aggiorna();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Caricamento non riuscito");
    } finally { setBusy(""); setTarget(null); }
  };

  const nomeDi = (r) => r.calcolati?.INCARICO_TITOLO || r.codice_documento || "Documento";

  return (
    <div data-testid="documenti-generati" className="min-w-0">
      {titolo && (
        <div className="subt">
          <span>{titolo}</span>
          <span className="text-[13px] font-normal normal-case tracking-normal text-[#5A5A5A]">{righe.length} file</span>
        </div>
      )}
      {righe.length === 0 ? (
        <div data-testid="documenti-generati-vuoto" className="riga text-[#5A5A5A]">
          Nessun documento generato con «Genera documento».
        </div>
      ) : righe.map((r) => {
        const st = STATI[r.stato] || STATI.generato;
        return (
          <div key={r.id} data-testid={`documento-generato-${r.id}`} className="riga group gap-3.5">
            <span className="flex-grow min-w-0 truncate" title={r.nome_file}>
              <span>{nomeDi(r)} · {(r.generato_il || "").slice(0, 10).split("-").reverse().join("/")}</span>
              <span className="text-[12px] text-[#5A5A5A]">
                {r.stato && r.stato !== "generato" ? ` · ${st.testo.toLowerCase()}` : ""}
                {r.forzatura ? " · " : ""}
                {r.forzatura && (
                  <span data-testid={`doc-forzato-${r.id}`} className="text-[#8C2A2A]"
                    title={`Generato con campi obbligatori vuoti · ${r.forzatura.da}${r.forzatura.nota ? " · " + r.forzatura.nota : ""}${r.forzatura.campi?.length ? " · campi: " + r.forzatura.campi.join(", ") : ""}`}>
                    forzato
                  </span>
                )}
                {r.generato_senza_documenti && (
                  <span data-testid={`doc-senza-doc-${r.id}`} className="text-[#9A3B13]"
                    title={`Mancavano: ${(r.documenti_mancanti || []).join(", ")}${r.motivo_documenti ? " · motivo: " + r.motivo_documenti : ""}`}>
                    {" · "}generato senza {(r.documenti_mancanti || []).length} document{(r.documenti_mancanti || []).length === 1 ? "o" : "i"}
                  </span>
                )}
                {r.avvisi?.length > 0 && <span data-testid={`doc-avvisi-${r.id}`} className="text-[#7a5c12]">{" · "}{r.avvisi.join(" · ")}</span>}
              </span>
            </span>
            <span className="flex items-center gap-2.5 shrink-0">
              {r.link_drive_cliente && (
                <a data-testid={`doc-link-${r.id}`} href={r.link_drive_cliente} target="_blank" rel="noreferrer"
                  className="text-[13px] font-semibold no-underline">Apri</a>
              )}
              {r.link_drive_firmato && (
                <a data-testid={`doc-link-firmato-${r.id}`} href={r.link_drive_firmato} target="_blank" rel="noreferrer"
                  title="Apri il PDF firmato" className="text-[#1F5C42]"><FileCheck2 className="w-4 h-4" /></a>
              )}
              <span className="hidden sm:flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                {r.stato === "da_salvare" && (
                  <button data-testid={`doc-riprova-${r.id}`} onClick={() => { setTarget(r); riprova.current?.click(); }}
                    disabled={busy === r.id} title="Riprova salvataggio su Drive" className="text-[#9b2c2c]">
                    {busy === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  </button>
                )}
                {r.stato !== "annullato" && (
                  <>
                    <button data-testid={`doc-inviato-${r.id}`} onClick={() => cambiaStato(r, "inviato")}
                      disabled={busy === r.id} title="Segna come inviato" className="text-[#5A5A5A] hover:text-[#A8863F]"><Send className="w-4 h-4" /></button>
                    <button data-testid={`doc-firmato-${r.id}`} onClick={() => { setTarget(r); firmato.current?.click(); }}
                      disabled={busy === r.id} title="Carica il PDF firmato" className="text-[#5A5A5A] hover:text-[#A8863F]"><Upload className="w-4 h-4" /></button>
                    <button data-testid={`doc-annulla-${r.id}`} onClick={() => cambiaStato(r, "annullato")}
                      disabled={busy === r.id} title="Annulla (il file resta su Drive)" className="text-[#5A5A5A] hover:text-[#8A2E2E]"><Ban className="w-4 h-4" /></button>
                  </>
                )}
              </span>
            </span>
          </div>
        );
      })}

      {nota && <div className="nota-tenue">{nota}</div>}

      <input ref={firmato} type="file" accept="application/pdf" className="hidden"
        data-testid="doc-firmato-input"
        onChange={(e) => { invia(e.target.files?.[0], "firmato"); e.target.value = ""; }} />
      <input ref={riprova} type="file" accept="application/pdf" className="hidden"
        data-testid="doc-riprova-input"
        onChange={(e) => { invia(e.target.files?.[0], "riprova"); e.target.value = ""; }} />
    </div>
  );
}
